import { useCallback, useEffect, useState } from "react";
import useOauth from "../../utils/customHooks/useOauth";
import { mintCableTicket } from "../../utils/mintCableTicket";
import { isConsumerActive, reconnectConsumer } from "./useActionCable";

const PROTOCOLS = ["actioncable-v1-json"];

// The server burns a ticket on connect and expires unused ones after 30
// seconds (CableTicket::TTL); stop trusting one a little before that.
const TICKET_FRESH_MS = 25_000;

// One ticket per page, shared by every channel on the API cable. The consumer
// reads url() on each socket open, which hands the ticket over and forgets it.
// Concurrent mints share one in-flight request.
const shared: {
  ticket: string;
  mintedAt: number;
  minting: Promise<string> | null;
} = {
  ticket: "",
  mintedAt: 0,
  minting: null,
};

function hasFreshTicket() {
  return (
    Boolean(shared.ticket) && Date.now() - shared.mintedAt < TICKET_FRESH_MS
  );
}

function takeTicket() {
  const ticket = hasFreshTicket() ? shared.ticket : "";
  shared.ticket = "";
  return ticket;
}

function mintShared(apiHost: string, token: string): Promise<string> {
  if (!shared.minting) {
    shared.minting = mintCableTicket(apiHost, token)
      .then((ticket) => {
        shared.ticket = ticket;
        shared.mintedAt = Date.now();
        return ticket;
      })
      .finally(() => {
        shared.minting = null;
      });
  }
  return shared.minting;
}

export function resetApiCableForTests() {
  shared.ticket = "";
  shared.mintedAt = 0;
  shared.minting = null;
}

// Connection options for useActionCable on the scopestack-api cable. Spread
// the result into useActionCable so every channel shares one websocket.
export function useApiCable(accountSlug: string) {
  const [authorizationCode] = useOauth();
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const websocketUrl = apiHost?.replace(/^https:\/\//, "wss://");
  const consumerKey = `api-cable:${accountSlug}`;
  const [ticketReady, setTicketReady] = useState(
    () => hasFreshTicket() || isConsumerActive(consumerKey)
  );

  useEffect(() => {
    if (!authorizationCode || !apiHost) return;
    if (hasFreshTicket() || isConsumerActive(consumerKey)) {
      setTicketReady(true);
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const tryMint = () => {
      if (cancelled) return;
      mintShared(apiHost, authorizationCode)
        .then(() => {
          if (cancelled) return;
          setTicketReady(true);
          // The socket may have dropped while nothing was subscribed, leaving
          // a stopped consumer behind; reopen it with the new ticket.
          reconnectConsumer(consumerKey);
        })
        .catch(() => {
          // A transient failure (network blip, brief 5xx) must not permanently
          // wedge real-time updates, so retry a few times with backoff.
          if (cancelled || attempts >= 5) return;
          attempts += 1;
          setTimeout(tryMint, Math.min(1000 * attempts, 5000));
        });
    };
    tryMint();

    return () => {
      cancelled = true;
    };
  }, [authorizationCode, apiHost, consumerKey]);

  const url = useCallback(
    () => `${websocketUrl}/cable?slug=${accountSlug}&ticket=${takeTicket()}`,
    [websocketUrl, accountSlug]
  );

  const onDisconnected = useCallback(async () => {
    if (!apiHost || !authorizationCode) return;
    try {
      await mintShared(apiHost, authorizationCode);
      // If the monitor's own reconnect already went out without a ticket, the
      // server rejected it and stopped the monitor; reopen with the new one.
      reconnectConsumer(consumerKey);
    } catch {
      /* no ticket to reconnect with; the next mount mints and reconnects */
    }
  }, [apiHost, authorizationCode, consumerKey]);

  return {
    url,
    enabled: ticketReady,
    protocols: PROTOCOLS,
    consumerKey,
    onDisconnected,
  };
}
